import { AssetDirectory, AssetFile, AssetHotspot, AssetItem, AssetLink, AssetMapArea, AssetPanoramaGroup, AssetPanoramaVersion, FileMap, IOrderedAssetItem, Navigation, StreetMap } from 'Api/Contracts/Dtos/Project/Assets';
import { AssetItemType } from 'Api/Contracts/Dtos/Project/Enums';

export const AssetItemSorter = (x: AssetItem, y: AssetItem): number => {
    const xType = x.type;
    const yType = y.type;

    if (xType != yType) {
        if (xType == AssetItemType.Project) {
            return -1;
        }
        if (yType == AssetItemType.Project) {
            return 1;
        }

        if (xType == AssetItemType.Navigation) {
            return -1;
        }
        if (yType == AssetItemType.Navigation) {
            return 1;
        }

        if (xType == AssetItemType.Directory) {
            return -1;
        }
        if (yType == AssetItemType.Directory) {
            return 1;
        }
    }

    if (isOrderedAssetItem(x) && isOrderedAssetItem(y)) {
        return x.order - y.order;
    }

    return x.name.localeCompare(y.name);
}

export function isOrderedAssetItem(assetItem: any): assetItem is IOrderedAssetItem {
    return 'order' in assetItem;
}

export function isFileMap(assetItem: AssetItem): assetItem is FileMap {
    return assetItem.type == AssetItemType.FileMap;
}

export function isStreetMap(assetItem: AssetItem): assetItem is StreetMap {
    return assetItem.type == AssetItemType.StreetMap;
}

export function isNavigation(assetItem: AssetItem): assetItem is Navigation {
    return assetItem.type == AssetItemType.Navigation;
}

export function isAssetDirectory(assetItem: AssetItem): assetItem is AssetDirectory {
    return assetItem.type == AssetItemType.Directory;
}

export function isAssetFile(assetItem: AssetItem): assetItem is AssetFile {
    return assetItem.type == AssetItemType.File;
}

export function isAssetHotspot(assetItem: AssetItem): assetItem is AssetHotspot {
    return assetItem.type == AssetItemType.Hotspot;
}

export function isAssetLink(assetItem: AssetItem): assetItem is AssetLink {
    return assetItem.type == AssetItemType.Link;
}

export function isAssetMapArea(assetItem: AssetItem): assetItem is AssetMapArea {
    return assetItem.type == AssetItemType.MapArea;
}

export function isAssetPanoramaGroup(assetItem: AssetItem): assetItem is AssetPanoramaGroup {
    return assetItem.type == AssetItemType.PanoramaGroup;
}

export function isAssetPanoramaVersion(assetItem: AssetItem): assetItem is AssetPanoramaVersion {
    return assetItem.type == AssetItemType.PanoramaVersion;
}


export function singleifyItemNames<T extends { name: string }>(collection: Array<T>, name: string): string {
    if (!collection.find(ai => ai.name == name)) {
        return name;
    }

    const regex = new RegExp(`^${name} \\((?<number>\\d+)\\)$`);

    let maxNumber: number = 0;

    for (let child of collection) {
        const number = parseInt(regex.exec(child.name)?.groups.number);

        if (!isNaN(number)) {
            maxNumber = Math.max(maxNumber, number);
        }
    }

    return `${name} (${maxNumber + 1})`;
}

/**
 * Browse recursively children of {@link AssetItem} if exists and filter {@link AssetPanoramaVersion} leafs
 * @param items Items to filter
 * @returns Filtered leaf and theirs ancestrors
 */
export function getPanoramaVersionAscendants(items: Array<AssetItem>): Array<AssetItem> {
    if (items.length == 0) {
        return [];
    }

    let filtered: Array<AssetItem> = [];

    for (const item of items) {
        if (isTypeValid(item)) {
            item.children = getPanoramaVersionAscendants(item.children);

            if (isEmptyDirectory(item)) {
                continue;
            }

            filtered.push(item);
        }
    }

    return filtered;

    function isEmptyDirectory(item: AssetDirectory): boolean {
        return item.type == AssetItemType.Directory
            && item.children.length == 0;
    }

    function isTypeValid(item: AssetItem): item is AssetDirectory {
        return item.type == AssetItemType.Project
            || item.type == AssetItemType.PanoramaGroup
            || item.type == AssetItemType.PanoramaVersion
            || item.type == AssetItemType.Directory;
    }
}
